import React, { useState, useEffect, createRef } from 'react';
import { graphql, Link } from 'gatsby';
import Img from 'gatsby-image';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import Main from '../components/Main';
import Container from '../components/Container';
import styles from '../cssmodules/insightsPreview.module.scss';
import Arrow from '../icons/link-arrow.svg';

function InsightsArchive({ data }) {
  const posts = data.allMarkdownRemark.edges;
  const [currentList, addToList] = useState([...posts.slice(0, 6)]);
  const [hasMorePosts, setMorePosts] = useState(true);

  const loadPostsRef = createRef();

  const loadPosts = () => {
    const currentLength = currentList.length;
    const morePosts = currentLength < posts.length;
    const nextPosts = morePosts
      ? posts.slice(currentLength, currentLength + 6)
      : [];
    setMorePosts(morePosts);
    addToList([...currentList, ...nextPosts]);
  };

  useEffect(() => {
    const callback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && hasMorePosts) {
          loadPosts();
        }
      });
    };
    const observer = new IntersectionObserver(callback);
    observer.observe(loadPostsRef.current);
    return () => {
      observer.disconnect();
    };
  });

  return (
    <Layout>
      <Seo
        title="Insights"
        description="A custom description"
        slug="/insights/"
      />
      <Main>
        <Container>
          <div className="pageHeader">
            <h1 className="heading1">Insights</h1>
          </div>
        </Container>
        <div className={styles.section}>
          <Container>
            <TransitionGroup className={styles.wrapper}>
              {currentList.map(post => {
                const { title, date, featureImage } = post.node.frontmatter;
                const { slug } = post.node.fields;
                return (
                  <CSSTransition
                    key={slug}
                    timeout={1000}
                    classNames="transition"
                  >
                    <Link to={slug} className={styles.preview} key={slug}>
                      <Img
                        sizes={{
                          ...featureImage.childImageSharp.fluid,
                          aspectRatio: 37 / 27,
                        }}
                      />
                      <div className={styles.content}>
                        <p className={styles.date}>
                          {date.toLocaleString('en-AU', { timeZone: 'UTC' })}
                        </p>
                        <h2 className="heading2">{title}</h2>
                      </div>
                      <div className={styles.action}>
                        <Arrow />
                      </div>
                    </Link>
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
            <div className={styles.loadPosts} ref={loadPostsRef}>
              {!hasMorePosts && <p>All posts loaded</p>}
            </div>
          </Container>
        </div>
      </Main>
    </Layout>
  );
}

export default InsightsArchive;

export const query = graphql`
  query {
    allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "insights" } } }
      sort: { fields: frontmatter___date, order: DESC }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            description
            title
            date(formatString: "DD/MM/YY")
            featureImage {
              childImageSharp {
                fluid(
                  maxWidth: 360
                  quality: 100
                  duotone: { highlight: "#EEF3F5", shadow: "#1D2C35" }
                ) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
  }
`;
